import React from 'react';
import { Alert, Box, Button, ButtonBase, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import { ActionRow, instructTargets } from './lib/selectors';
import { ActionItem, DecisionValue, Snapshot } from './lib/types';
import { ink, tint } from './lib/tone';
import { WARN_TEXT, RADIUS, TONE } from './lib/brand';
import { fmtIct } from './lib/ict';
import { DecisionStateChip } from './chips';
import { useBotOpsActions } from './BotOpsActions';
import { InstructComposer } from './InstructComposer';
import { CHAT_PEOPLE, CHAT_STATUS_LABEL, ChatPerson, PERSON_LABEL, buildGchatMessage, gchatDefaults, togglePerson } from './lib/gchat';

const IMPL_LABEL: Record<string, string> = {
  not_started: 'Not started', queued: 'Queued', in_progress: 'In progress', done: 'Done', failed: 'Failed', blocked: 'Blocked',
};

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

/** The Implementation Bot's progress on an approved item, from its act_<id> replies. */
export function ImplementationChip({ action }: { action: ActionItem }) {
  const impl = action.implementation;
  if (!impl || action.decision.value !== 'yes' || impl.status === 'not_started') return null;
  const bad = impl.status === 'failed' || impl.status === 'blocked';
  const done = impl.status === 'done';
  return (
    <Box
      data-testid="impl-chip"
      title={impl.notes || undefined}
      sx={{ display: 'inline-block', mt: 0.5, fontSize: 11, fontWeight: 600, px: 1, py: 0.2, borderRadius: RADIUS.pill, color: bad ? TONE.warn.fg : done ? TONE.good.fg : TONE.info.fg, bgcolor: bad ? TONE.warn.bg : done ? TONE.good.bg : TONE.info.bg }}
    >
      Implementation: {IMPL_LABEL[impl.status] || impl.status}
    </Box>
  );
}

/** "💬 Grokbot Chat: queued → posted (time)" / "failed · Retry" / "blocked". */
export function ChatChip({ action, canRetry }: { action: ActionItem; canRetry: boolean }) {
  const api = useBotOpsActions();
  const [msg, setMsg] = React.useState<string | null>(null);
  const chat = action.gchat_chat;
  if (!chat || chat.status === 'cancelled') return null;
  const bad = chat.status === 'failed' || chat.status === 'blocked';
  const who = (chat.recipients || []).map(PERSON_LABEL).join(', ');
  return (
    <Box data-testid="chat-chip" data-status={chat.status} sx={{ mt: 0.6, fontSize: 11.5, fontWeight: 600, color: bad ? WARN_TEXT : chat.status === 'posted' ? ink(TONE.good.fg, TONE.good.border) : ink(TONE.info.fg, TONE.info.border) }}>
      <span>💬 Grokbot Chat: {CHAT_STATUS_LABEL[chat.status]}{chat.status === 'posted' && chat.posted_at ? ` (${fmtIct(chat.posted_at)})` : ''}</span>
      {who && <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}> · {who}</Box>}
      {chat.status === 'failed' && canRetry && (
        <>
          {' · '}
          <ButtonBase onClick={async () => setMsg((await api.retryChat(action.id)).message)} sx={{ fontSize: 'inherit', fontWeight: 700, textDecoration: 'underline', color: 'inherit' }}>Retry</ButtonBase>
        </>
      )}
      {msg && <Box role="status" component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}> · {msg}</Box>}
    </Box>
  );
}

/**
 * Yes / No / Instruct instead for one action row (and the same control in the
 * recommendation drawer). Yes and No ask for a confirmation first; the chip then
 * changes straight away and Undo is one click. A blocker is not decidable. Anyone
 * but the super admin sees the read-only state.
 */
export function DecisionControl({
  row, snapshot, onOpenInstruction, layout = 'row',
}: {
  row: ActionRow; snapshot: Snapshot; onOpenInstruction: (id: string) => void; layout?: 'row' | 'drawer';
}) {
  const api = useBotOpsActions();
  const { action, bot } = row;
  const ui = api.decisionUi(action.id);
  const [confirm, setConfirm] = React.useState<'yes' | 'no' | null>(null);
  const [instructing, setInstructing] = React.useState(false);
  const confirmRef = React.useRef<HTMLButtonElement | null>(null);
  const defaults = React.useMemo(() => gchatDefaults(snapshot, bot, action), [snapshot, bot, action]);
  const [chatOn, setChatOn] = React.useState<boolean>(Boolean(action.gchat_post));
  const [people, setPeople] = React.useState<ChatPerson[]>(defaults.stakeholders);
  const message = React.useMemo(() => buildGchatMessage({ people, topic: defaults.topic, bot, action }), [people, defaults.topic, bot, action]);
  const isDefault = people.length === defaults.stakeholders.length && people.every((p) => defaults.stakeholders.includes(p));

  React.useEffect(() => { if (confirm && confirmRef.current) confirmRef.current.focus(); }, [confirm]);

  const value: DecisionValue = ui.optimistic || (action.decision && action.decision.value) || 'pending';
  const sx = layout === 'drawer' ? { size: 'medium' as const } : { size: 'small' as const };

  if (!action.decidable) {
    return <Typography variant="caption" color="text.secondary">No decision: blocker, not a proposal. See the bot drawer.</Typography>;
  }

  const stamp = (
    action.decision && action.decision.decided_at && !ui.optimistic ? (
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.4 }}>
        {fmtIct(action.decision.decided_at)}{action.decision.decided_by ? ` · ${action.decision.decided_by}` : ''}
      </Typography>
    ) : null
  );

  if (!api.canWrite) {
    return value === 'pending'
      ? <Typography variant="caption" color="text.secondary">Pending decision</Typography>
      : <Box><DecisionStateChip value={value} onClick={value === 'instructed' && action.decision.instruction_id ? () => onOpenInstruction(action.decision.instruction_id as string) : undefined} />{stamp}<ImplementationChip action={action} /><ChatChip action={action} canRetry={false} /></Box>;
  }

  const decide = async (v: 'yes' | 'no' | 'pending') => {
    setConfirm(null);
    const chat = v === 'yes' && chatOn ? { post: true as const, stakeholders: people, message } : null;
    await api.decide(action.id, v, chat);
  };

  return (
    <Box data-testid={`decision-${action.id}`} onClick={stop} onKeyDown={stop}>
      {ui.saving && (
        <Box role="status" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
          <CircularProgress size={12} thickness={6} /> Saving…
        </Box>
      )}

      {ui.error && (
        <Alert
          severity="error"
          role="alert"
          sx={{ py: 0, mb: 0.8, alignItems: 'center' }}
          action={(
            <>
              <Button size="small" color="inherit" onClick={() => api.retryDecision(action.id)}>Retry</Button>
              <Button size="small" color="inherit" onClick={() => api.dismissDecisionError(action.id)}>Dismiss</Button>
            </>
          )}
        >
          Couldn&apos;t save. {ui.error}
        </Alert>
      )}

      {value !== 'pending' && (
        <Box>
          <DecisionStateChip value={value} onClick={value === 'instructed' && action.decision.instruction_id ? () => onOpenInstruction(action.decision.instruction_id as string) : undefined} />
          {stamp}
          <ImplementationChip action={action} />
          <ChatChip action={action} canRetry />
          {(value === 'yes' || value === 'no') && (
            <Box sx={{ mt: 0.5 }}>
              <Button size="small" variant="text" disabled={ui.saving} onClick={() => decide('pending')} sx={{ textTransform: 'none', px: 0.5, minWidth: 0 }}>Undo</Button>
            </Box>
          )}
        </Box>
      )}

      {(value === 'pending' || value === 'instructed') && !confirm && !instructing && (
        <Box role="group" aria-label="Your decision" sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: value === 'instructed' ? 0.8 : 0 }}>
          <Button {...sx} variant="contained" color="success" disabled={ui.saving} onClick={() => setConfirm('yes')} sx={{ textTransform: 'none', minWidth: 0 }}>Yes</Button>
          <Button {...sx} variant="outlined" color="inherit" disabled={ui.saving} onClick={() => setConfirm('no')} sx={{ textTransform: 'none', minWidth: 0 }}>No</Button>
          {value === 'pending' && (
            <Button {...sx} variant="outlined" disabled={ui.saving} onClick={() => setInstructing(true)} sx={{ textTransform: 'none' }}>Instruct instead</Button>
          )}
        </Box>
      )}

      {confirm && (
        <Box
          role="group"
          aria-label={`Confirm ${confirm === 'yes' ? 'Yes' : 'No'}`}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Escape') setConfirm(null); }}
          sx={{ border: '1px solid', borderColor: confirm === 'yes' ? TONE.good.border : 'divider', bgcolor: confirm === 'yes' ? tint(TONE.good.bg) : 'action.hover', borderRadius: RADIUS.lg, p: 1 }}
        >
          <Typography sx={{ fontSize: 12.5, mb: 0.8 }}>
            {confirm === 'yes'
              ? (chatOn
                ? 'Approve, queue for the Implementation Bot, and post ONE message to Grokbot Chat?'
                : `Approve this? It is queued for the Implementation Bot${bot ? ` (owner: ${bot.name})` : ''}. Nothing executes without your exact approval for production, GitHub, money or external sends.`)
              : 'Decline this? The owner bot is told (FYI) and nothing is queued.'}
          </Typography>
          {confirm === 'yes' && (
            <Box sx={{ mb: 0.8 }}>
              <FormControlLabel
                control={<Switch size="small" checked={chatOn} onChange={(e) => setChatOn(e.target.checked)} />}
                label={<Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>Also post to Grokbot Chat</Typography>}
              />
              {chatOn && (
                <Box data-testid="chat-options">
                  <Box role="group" aria-label="Chat stakeholders" sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', my: 0.6 }}>
                    {CHAT_PEOPLE.map((p) => (
                      <ButtonBase
                        key={p}
                        aria-pressed={people.includes(p)}
                        onClick={() => setPeople((cur) => togglePerson(cur, p))}
                        sx={{ fontSize: 11.5, fontWeight: 600, px: 1, py: 0.3, borderRadius: RADIUS.pill, border: '1px solid', borderColor: people.includes(p) ? 'primary.main' : 'divider', bgcolor: people.includes(p) ? 'primary.main' : 'transparent', color: people.includes(p) ? 'primary.contrastText' : 'text.primary' }}
                      >
                        {PERSON_LABEL(p)}
                      </ButtonBase>
                    ))}
                  </Box>
                  {isDefault && <Typography variant="caption" color="text.secondary" component="div">Default for {defaults.topic} · GOVERNANCE §19</Typography>}
                  <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 700 }}>Recipients: {people.map(PERSON_LABEL).join(', ')}</Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>Exact Chat message (posted once, only if the safety scan passes):</Typography>
                  <Box component="pre" data-testid="chat-preview" sx={{ m: 0, mt: 0.3, p: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: RADIUS.md, fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' }}>{message}</Box>
                </Box>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 0.8 }}>
            <Button ref={confirmRef} {...sx} variant="contained" color={confirm === 'yes' ? 'success' : 'inherit'} onClick={() => decide(confirm)} sx={{ textTransform: 'none' }}>
              Confirm {confirm === 'yes' ? (chatOn ? 'Yes + post to Chat' : 'Yes') : 'No'}
            </Button>
            <Button {...sx} onClick={() => setConfirm(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          </Box>
        </Box>
      )}

      {instructing && (
        <Box sx={{ mt: 0.5 }}>
          <InstructComposer
            label="Instruct instead"
            kind="override"
            relatedActionId={action.id}
            targets={instructTargets(snapshot, bot)}
            placeholder="What should the bot do instead?"
            submitLabel="Send instead"
            autoFocus
            onCancel={() => setInstructing(false)}
            onSent={(i) => { setInstructing(false); onOpenInstruction(i.id); }}
          />
        </Box>
      )}
    </Box>
  );
}
